import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { type UpdateChainBody } from "../api/chains";
import { useUpdateChain } from "../hooks/useUpdateChain";
import { useDeleteChain } from "../hooks/useDeleteChain";
import {
  useStaticSystemsById,
  useStaticSystemsSearch,
  type SystemSearchResult,
} from "../hooks/useStaticSystems";
import { ACCESS_LABELS, ACCESS_TIERS, isJspaceClass } from "../types/chain";
import type { ChainAccess, ChainSummary } from "../types/chain";

interface ChainSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chain: ChainSummary;
  ownedRootSystemIds: Map<string, string>;
  onUpdated: (chain: ChainSummary) => void;
  onDeleted: () => void;
}

const ACCESS_HINTS: Record<ChainAccess, string> = {
  private: "Visible only to you",
  corp: "Visible to your corporation",
  alliance: "Visible to your alliance",
  public: "Visible to anyone signed in",
};

export function ChainSettingsModal({
  open,
  onOpenChange,
  chain,
  ownedRootSystemIds,
  onUpdated,
  onDeleted,
}: ChainSettingsModalProps) {
  const staticById = useStaticSystemsById();
  const staticSystems = useStaticSystemsSearch();
  const updateChain = useUpdateChain();
  const deleteChain = useDeleteChain();

  const [name, setName] = useState(chain.name);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SystemSearchResult | null>(null);
  const [rootInitialized, setRootInitialized] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<ChainAccess>(chain.access);

  useEffect(() => {
    if (rootInitialized) return;
    const root = staticById.byId(chain.root_system_id);
    if (!root) return;
    setSelected({
      id: root.id,
      name: root.name,
      class: root.class,
      region: root.region,
    });
    setQuery(root.name);
    setRootInitialized(true);
  }, [rootInitialized, staticById, chain.root_system_id]);

  useEffect(() => {
    if (!open) {
      setName(chain.name);
      setAccess(chain.access);
      setQuery("");
      setSelected(null);
      setRootInitialized(false);
      setConfirmDelete(false);
      setError(null);
      updateChain.reset();
      deleteChain.reset();
    }
  }, [open, chain.name, chain.access, updateChain, deleteChain]);

  const results = useMemo(() => {
    const showResults =
      query.trim().length > 0 && (!selected || selected.name !== query);
    if (!showResults) return [];
    return staticSystems
      .search(query, 100)
      .filter((r) => isJspaceClass(r.class))
      .slice(0, 20);
  }, [query, selected, staticSystems]);

  const ownedConflictName = selected
    ? ownedRootSystemIds.get(selected.id) ?? null
    : null;

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== chain.name;
  const rootChanged = selected != null && selected.id !== chain.root_system_id;
  const accessChanged = access !== chain.access;
  const hasChanges = nameChanged || rootChanged || accessChanged;
  const busy = updateChain.isPending || deleteChain.isPending;
  const submitDisabled = trimmedName.length === 0 || !hasChanges || busy;

  const handlePick = (r: SystemSearchResult) => {
    setSelected(r);
    setQuery(r.name);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;
    const body: UpdateChainBody = {};
    if (nameChanged) body.name = trimmedName;
    if (rootChanged) body.root_system_id = Number(selected!.id);
    if (accessChanged) body.access = access;
    setError(null);
    updateChain.mutate(
      { id: chain.id, body },
      {
        onSuccess: (updated) => onUpdated(updated),
        onError: (err) => setError(err.message),
      },
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setError(null);
    deleteChain.mutate(chain.id, {
      onSuccess: () => onDeleted(),
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal__overlay" />
        <Dialog.Content className="modal__content" aria-describedby={undefined}>
          <Dialog.Title className="modal__title">
            <Pencil size={16} aria-hidden /> Chain settings
          </Dialog.Title>
          <form className="form modal__form" onSubmit={handleSubmit}>
            <div className="form__row">
              <label className="form__label" htmlFor="chain-settings-name">
                Name
              </label>
              <input
                id="chain-settings-name"
                className="form__input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form__row">
              <label className="form__label" htmlFor="chain-settings-root">
                Root system
              </label>
              <input
                id="chain-settings-root"
                className="form__input"
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                }}
                placeholder="Type to search…"
                autoComplete="off"
              />
              {results.length > 0 && (
                <ul className="picker-results">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="picker-results__item"
                        onClick={() => handlePick(r)}
                      >
                        {r.name} ({r.class}
                        {r.region ? `, ${r.region}` : ""})
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {ownedConflictName && (
                <p className="form__hint form__hint--warn">
                  You already have a chain rooted at {selected!.name}:{" "}
                  {ownedConflictName}
                </p>
              )}
            </div>

            <div className="form__row">
              <span className="form__label">Visibility</span>
              <div className="radio-group">
                {ACCESS_TIERS.map((tier) => (
                  <label key={tier} className="radio-group__option">
                    <input
                      type="radio"
                      name="chain-access"
                      value={tier}
                      checked={access === tier}
                      onChange={() => setAccess(tier)}
                    />
                    {ACCESS_LABELS[tier]}
                  </label>
                ))}
              </div>
              <p className="form__hint">{ACCESS_HINTS[access]}</p>
            </div>

            {error && <p className="form__hint form__hint--error">{error}</p>}

            <div className="modal__actions">
              <button
                type="button"
                className="modal__btn"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal__btn modal__btn--primary"
                disabled={submitDisabled}
              >
                {updateChain.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>

          <div className="modal__danger">
            <span className="modal__danger-label">Danger zone</span>
            <button
              type="button"
              className="modal__btn modal__btn--danger"
              onClick={handleDelete}
              disabled={busy}
            >
              {deleteChain.isPending
                ? "Deleting…"
                : confirmDelete
                  ? "Click again to confirm"
                  : "Delete chain"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
